import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  TextField,
} from "@mui/material";
import pluralize from "pluralize";
import { RiCloseLine } from "@remixicon/react";
import { useTheme } from "@mui/material/styles";

import {
  RemoveDeliveriesPayloadT,
  ScheduleDialogProps,
} from "../../../types/ScheduleForm";
import useGaneshApi from "../../../hooks/useGaneshApi";
import { AuthContextType, useAuth } from "../../../contexts/AuthContext";

import DeliveriesCombo from "./DeliveriesCombo";
import { generateFormLabelTypographyProps } from "./FormLabelProps";

export default function DeliveriesRemoveDialog({
  onClose,
  selectedRows = [],
  onSuccessCallback,
}: ScheduleDialogProps) {
  const userAuth: AuthContextType = useAuth();
  const { apiRequest } = useGaneshApi();
  const { date_id } = useParams<{ date_id: string }>();
  const theme = useTheme();

  const formLabelTypographyProps = generateFormLabelTypographyProps(theme);

  const { mutate: removeDeliveries, isPending: isRemovingDeliveries } =
    useMutation({
      mutationFn: async (payload: RemoveDeliveriesPayloadT) => {
        const response = await apiRequest("/schedule/batch-remove-deliveries", {
          method: "DELETE",
          body: payload,
        });
        return response as any;
      },
      onSuccess: (data, variables) => {
        onSuccessCallback?.();
      },
      onError: (error) => {
        console.error(error);
      },
    });

  const handleSubmit = () => {
    const payload: RemoveDeliveriesPayloadT = {
      admin_id: userAuth.authAttributes?.id ?? null,
      delivery_schedule_ids: selectedRows.map((item) => item.delivery_fulfillment_id),
    };

    removeDeliveries(payload);
  };

  const renderButtonLabel = () => {
    const description = `${pluralize("delivery", selectedRows.length)}`;
    if (isRemovingDeliveries) {
      return `Removing ${description}`;
    }
    return `Remove ${description}`;
  };

  return (
    <>
      <DialogTitle>
        Remove {`${pluralize("delivery", selectedRows.length)}`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "24px",
            alignItems: "start",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <FormLabel htmlFor="date" {...formLabelTypographyProps}>
            Fulfillment Date *
          </FormLabel>
          <TextField
            id="date"
            type="date"
            size="small"
            fullWidth
            defaultValue={date_id}
            disabled={true}
          />
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Deliveries *
          </FormLabel>
          <DeliveriesCombo
            selectedItems={selectedRows}
            onSelectionChange={() => {}}
            action={"REMOVE"}
          />
          <DialogActions
            style={{
              gridColumn: "1 / -1",
              justifyContent: "end",
              marginTop: "16px",
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              sx={{ minWidth: "120px" }}
              disabled={!selectedRows.length || isRemovingDeliveries}
            >
              {renderButtonLabel()}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </>
  );
}
